import {useState} from "react";
import moment from "moment";
import {useNavigate, useSearchParams} from "react-router-dom";
import {Checkbox, FormControlLabel} from "@mui/material";

import Page from "@/components/Page";
import {
	DsActivity,
	DsChips,
	DsEmpty,
	DsHeader,
	DsKpi,
	DsKpiRow,
	DsMissingSources,
	DsSection,
	DsSkeleton,
} from "@/components/DsKit";

import {useClubCertificates, useClubSnapshot} from "@/utils/hooks";
import {
	CONTROL_DEPARTMENTS,
	buildActivities,
	loadSeenActivities,
	saveSeenActivities,
} from "@/utils/clubControl";

function DsActivitiesPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const department = searchParams.get("reparto") || "all";

	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({handleNotification});
	const {data: certificates} = useClubCertificates({snapshot: s});

	const [seen, setSeen] = useState(loadSeenActivities);
	const [showSeen, setShowSeen] = useState(false);

	const [showExamples, setShowExamples] = useState(false);
	const activities = buildActivities(s, certificates, {
		includePreview: showExamples,
	});
	const open = activities.filter((a) => !a.preview && !seen[a.id]);
	const visible = activities.filter(
		(a) =>
			(department === "all" ||
				(department === "decisioni"
					? a.decision
					: a.department === department)) &&
			(showSeen || !seen[a.id]),
	);
	const dueSoon = open.filter(
		(a) => a.dueAt && moment(a.dueAt).diff(moment(), "days") <= 7,
	);

	const toggleSeen = (id) => {
		const next = {...seen};
		if (next[id]) delete next[id];
		else next[id] = new Date().toISOString();
		setSeen(next);
		saveSeenActivities(next);
	};

	const chipOptions = [
		{value: "all", label: "Tutti i reparti", count: open.length},
		{
			value: "decisioni",
			label: "Le mie decisioni",
			count: open.filter((a) => a.decision).length,
		},
		...CONTROL_DEPARTMENTS.map((d) => ({
			value: d.id,
			label: d.label,
			count: open.filter((a) => a.department === d.id).length,
		})).filter((o) => o.count > 0 || o.value === department),
	];

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Attività aperte"
					subtitle="Tutto ciò che è in corso nei reparti e richiede attenzione"
					snapshot={s}
					scope={scope}
					onReload={reload}
					reloading={loading}
				/>
				<DsSkeleton visible={loading && !s} />
				{s && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsKpiRow>
							<DsKpi label="Aperte" value={open.length} />
							<DsKpi
								label="Urgenti"
								value={open.filter((a) => a.severity === "high").length}
								tone="red"
							/>
							<DsKpi
								label="In scadenza"
								value={dueSoon.length}
								sub="entro 7 giorni"
								tone={dueSoon.length ? "yellow" : undefined}
							/>
							<DsKpi
								label="Prese in visione"
								value={
									activities.filter((a) => !a.preview && seen[a.id]).length
								}
							/>
						</DsKpiRow>

						<div className="ds-toolbar">
							<DsChips
								options={chipOptions}
								value={department}
								onChange={(v) =>
									setSearchParams(v === "all" ? {} : {reparto: v})
								}
							/>
							<FormControlLabel
								className="ds-muted"
								control={
									<Checkbox
										size="small"
										checked={showExamples}
										onChange={(e) => setShowExamples(e.target.checked)}
									/>
								}
								label="Mostra esempi dimostrativi (esclusi dai KPI)"
							/>
							<FormControlLabel
								className="ds-muted"
								control={
									<Checkbox
										size="small"
										checked={showSeen}
										onChange={(e) => setShowSeen(e.target.checked)}
									/>
								}
								label="Mostra anche quelle già viste"
							/>
						</div>

						{CONTROL_DEPARTMENTS.filter((d) =>
							visible.some((a) => a.department === d.id),
						).map((d) => (
							<DsSection
								key={d.id}
								title={d.label}
								hint={`${visible.filter((a) => a.department === d.id).length} attività`}
							>
								{visible
									.filter((a) => a.department === d.id)
									.map((a) => (
										<DsActivity
											key={a.id}
											item={a}
											seen={!!seen[a.id]}
											onToggleSeen={() => toggleSeen(a.id)}
											onOpen={a.path ? () => navigate(a.path) : undefined}
										/>
									))}
							</DsSection>
						))}
						{!visible.length && (
							<DsEmpty>
								Nessuna attività aperta
								{department !== "all" ? " in questo reparto" : ""}. Tutto sotto
								controllo.
							</DsEmpty>
						)}
					</>
				)}
			</div>
		</Page>
	);
}

export default DsActivitiesPage;
